import React, { useEffect, useState } from "react";
import { HandCoins, Wallet, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { rupiah, fmtDate } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, StatCard, Button, Modal, Badge } from "@/components/common";

export default function SupplierDebts() {
  const { can, isOwner } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);
  const canPay = can("expenses", "create") || isOwner;

  const load = async () => {
    setLoading(true);
    try { setData(await api.get("/supplier-debts")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (d) => {
    if (!window.confirm(`Hapus tagihan hutang ${d.supplier} (${rupiah(d.balance_due)})?\n\nTagihan ini akan hilang dari laporan Hutang Supplier.`)) return;
    try { await api.del(`/supplier-debts/${d.id}`); toast.success("Hutang dihapus"); load(); }
    catch (e) { toast.error(e.message); }
  };

  if (loading) return <Loading />;

  return (
    <>
      <PageHeader title="Hutang Supplier" subtitle={`${data?.items?.length || 0} tagihan`} icon={HandCoins} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard label="Total Hutang" value={rupiah(data?.total_outstanding || 0)} tone="danger" />
        <StatCard label="Jatuh Tempo" value={rupiah(data?.overdue_total || 0)} sub={`${data?.overdue_count || 0} tagihan`} tone="warning" />
        <StatCard label="Supplier" value={data?.by_supplier?.length || 0} />
      </div>

      {!data?.items?.length ? <EmptyState icon={HandCoins} title="Tidak ada hutang" message="Semua pembelian sudah lunas." /> : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
              <th className="py-3 px-4 font-medium">Supplier / Invoice</th><th className="py-3 px-4 font-medium">Jatuh Tempo</th>
              <th className="py-3 px-4 font-medium text-right">Total</th><th className="py-3 px-4 font-medium text-right">Sisa</th>{(canPay || isOwner) && <th className="py-3 px-4"></th>}
            </tr></thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id} className="border-b border-slate-50" data-testid={`debt-${d.id}`}>
                  <td className="py-3 px-4"><div className="font-medium text-slate-800">{d.supplier}</div><div className="text-xs text-slate-400">{d.shipment_no || d.invoice_no || d.debt_no}</div></td>
                  <td className="py-3 px-4">{d.due_date ? fmtDate(d.due_date) : "-"}</td>
                  <td className="py-3 px-4 text-right text-slate-600">{rupiah(d.total_cost)}</td>
                  <td className="py-3 px-4 text-right font-semibold text-rose-600">{rupiah(d.balance_due)}</td>
                  {(canPay || isOwner) && <td className="py-3 px-4 text-right">
                    <div className="flex gap-1 justify-end">
                      {canPay && <Button size="sm" variant="outline" onClick={() => setPaying(d)}><Wallet size={14} /> Bayar</Button>}
                      {isOwner && <button onClick={() => del(d)} className="w-8 h-8 rounded-lg hover:bg-rose-50 flex items-center justify-center text-rose-500" data-testid={`delete-debt-${d.id}`} title="Hapus"><Trash2 size={15} /></button>}
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {paying && <PayModal debt={paying} onClose={() => setPaying(null)} onSaved={() => { setPaying(null); load(); }} />}
    </>
  );
}

function PayModal({ debt, onClose, onSaved }) {
  const [amount, setAmount] = useState(String(debt.balance_due));
  const [method, setMethod] = useState("transfer");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!Number(amount) || Number(amount) <= 0) return toast.error("Jumlah tidak valid");
    setBusy(true);
    try { await api.post(`/supplier-debts/${debt.id}/payments`, { amount: Number(amount), method }); toast.success("Pembayaran dicatat"); onSaved(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title={`Bayar Hutang · ${debt.supplier}`} maxWidth="max-w-sm">
      <div className="space-y-3">
        <div className="text-sm text-slate-500">Sisa: <b className="text-rose-600">{rupiah(debt.balance_due)}</b></div>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Jumlah</span><input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300" data-testid="pay-amount" /></label>
        <label className="block"><span className="text-sm font-medium text-slate-600 mb-1 block">Metode</span><select value={method} onChange={(e) => setMethod(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-slate-300"><option value="transfer">Transfer</option><option value="cash">Tunai</option></select></label>
        <Button className="w-full" onClick={save} disabled={busy} data-testid="pay-save">{busy ? "Menyimpan..." : "Bayar"}</Button>
      </div>
    </Modal>
  );
}
